<template>
    <div style="width: 100%; height: calc(100% - 48px); display: flex; flex-direction: column; justify-content: stretch">
        <div style="flex-grow: 0; padding: 10px; display: flex;">
            <div style="width: 500px;">
                <search-bar
                        reactivity="input"
                        v-model="search" placeholder="검색"></search-bar>
            </div>
            <v-spacer></v-spacer>
        </div>
        <div style="flex-grow: 1; overflow-y: auto; height: 100%; display: flex; justify-content: center; align-items: center;">
            <template v-if="!loading">
                <manual-tree-view
                        :sort="sort"
                        :search="search"
                        :items="items"
                        @click:delete="onDeleteClicked"
                        @click:restore="onRestoreClicked"
                        @click:delete-permanently="onDeletePermanentlyClicked"
                        @click:manage-item="onClickManageItem"
                        @click:activate="onClickActivate"
                        @click:close="onClickClose"
                >
                </manual-tree-view>
            </template>
            <template v-else>
                <div>
                    <v-progress-circular indeterminate size="48" :color="'primary'"
                    ></v-progress-circular>
                    <div class="mt-3">로드중...</div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import {PATH} from "@/constants";
    import SearchBar from "@/components/input/SearchBar";
    import {ManualTree} from "@/assets/plugins/mps-common/manual/manual-manager";
    import ManualTreeView from "@/views/manual/manage/ManualTreeView";
    import OnManualReceivedListener from "@/framework/listeners/OnManualReceivedListener";

    export default {
        name: "ManualManageTabContent",
        components: {ManualTreeView, SearchBar},
        mixins: [OnManualReceivedListener],
        props: {
            sort: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                search: "",
                loading: false,
                /**
                 * 서버에서 수신한 전체 매뉴얼
                 */
                manuals: [],
                /**
                 * 매뉴얼을 트리 형태로 관리해주는 객체
                 */
                manualTree: undefined,
            }
        },
        computed: {
            items() {
                if (!this.manualTree) return [];
                return this.manualTree.items;
            }
        },
        created() {
            this.loadManuals();
        },
        methods: {
            async loadManuals() {
                this.loading = true;
                const response = await this.$request(PATH.MANUAL.LIST)
                    .setObject({sort: this.sort})
                    .enqueue();
                this.manualTree = new ManualTree(response.data);
                this.loading = false;
            },

            /**
             * 매뉴얼을 복구합니다.
             *
             * 매뉴얼을 복구하면 부모 노드의 매뉴얼까지 모두 복구됩니다.
             */
            onRestoreClicked(node) {
                this.$confirm('매뉴얼을 복구합니다.<br><b>모든 부모 매뉴얼 또한 복구됩니다.</b><br>복구하시겠습니까?')
                    .then(() => this.requestRestore(node));
            },

            /**
             * 서버에 매뉴얼 복구 요청을 합니다.
             */
            async requestRestore(node) {
                await this.$requestWithOverlay(PATH.MANUAL.RESTORE)
                    .setObject({id: node.id})
                    .enqueue();
                await this.loadManuals();
            },

            /**
             * 매뉴얼을 삭제처리힙니다.
             *
             * 자식의 매뉴얼도 모두 삭제처리됩니다.
             *
             * 물리적인 DB 에서 실제로 삭제되지는 않지만 플래그로 구분합니다.
             */
            onDeleteClicked(node) {
                this.$confirm('매뉴얼을 삭제처리합니다.<br><b>물리 DB 에서 삭제되지는 않지만 플래그로 삭제 처리합니다.<br>삭제처리된 매뉴얼은 유저들이 접근하지 못합니다.</b>')
                    .then(() => this.requestDelete(node));
            },

            /**
             * 서버에 매뉴얼 삭제 처리 요청을 합니다.
             */
            async requestDelete(node) {
                await this.$requestWithOverlay(PATH.MANUAL.DELETE)
                    .setObject({id: node.id})
                    .enqueue();
                await this.loadManuals();
            },

            /**
             * 매뉴얼을 영구적으로 삭제합니다.
             *
             * 매뉴얼을 영구적으로 삭제하면 자식 노드의 매뉴얼까지 모두 DB에서 물리적으로 삭제됩니다.
             */
            onDeletePermanentlyClicked(node) {
                this.$confirm('매뉴얼을 DB에서 물리적으로 삭제합니다.<br><b>모든 자식 매뉴얼이 제거되며,<br>절대로 다시 되돌릴 수 없습니다.</b><br> 그리고 여기에 속한 컨텐츠 또한 모두 삭제됩니다.')
                    .then(() => this.$confirm("정말로 영구적으로 삭제하시겠습니까?")
                        .then(() => this.requestDeletePermanently(node)));
            },

            /**
             * 서버에 매뉴얼 영구 삭제 요청을 합니다.
             */
            async requestDeletePermanently(node) {
                await this.$requestWithOverlay(PATH.MANUAL.DELETE_PERMANENTLY)
                    .setObject({id: node.id})
                    .enqueue();
                await this.loadManuals();
            },

            /**
             * 매뉴얼이 추가, 삭제, 업데이트 되었습니다.
             */
            async onManualReceived() {
                await this.loadManuals();
            },

            /**
             * 해당 매뉴얼의 컨텐츠를 관리하기 위해 라우터를 이동합니다.
             */
            onClickManageItem(node) {
                this.$router.push({
                    name: "managertnbox.manual.item.manage",
                    params: {name: node.name,},
                    query: {title: node.manual.title}
                });
            },

            /**
             * 해당 매뉴얼을 활성화 시킵니다.
             */
            onClickActivate(node) {
                this.$confirm('매뉴얼을 활성화합니다.<br><b>활성화된 매뉴얼은 사용자에게 노출되어집니다.<br>모든 부모 노드가 활성화됩니다.</b><br>활성화 하시겠습니까?')
                    .then(() => this.requestActivate(node));
            },

            /**
             * 활성화 요청을 서버로 보냅니다.
             */
            async requestActivate(node) {
                await this.$requestWithOverlay(PATH.MANUAL.ACTIVATE)
                    .setObject({id: node.id})
                    .enqueue();
                await this.loadManuals();
            },

            /**
             * 해당 매뉴얼을 활성화 시킵니다.
             */
            onClickClose(node) {
                this.$confirm('<span style="color: red;">매뉴얼을 비활성화합니다.</span><br><b>비활성화된 매뉴얼은 사용자가 접근할 수 없습니다.<br>' +
                    '모든 <span style="color: red;">자식 노드</span>가 비활성화됩니다.</b><br>비활성화 하시겠습니까?')
                    .then(() => this.requestClose(node));
            },

            /**
             * 활성화 요청을 서버로 보냅니다.
             */
            async requestClose(node) {
                await this.$requestWithOverlay(PATH.MANUAL.CLOSE)
                    .setObject({id: node.id})
                    .enqueue();
                await this.loadManuals();
            },
        },
    }
</script>

<style scoped>

</style>